import { Injectable, Injector } from '@angular/core';
import { Observable, throwError, from } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { NavController, ToastController } from '@ionic/angular/standalone';
import { ErrorHandlerUtility, EnhancedError } from '../../utilities/error-handler.utility';
import { AuthService } from '../auth/auth.service';

export type Params = {
  [key: string]: string | string[] | number | boolean;
};

const dateRegex: RegExp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.)?\d{0,8}Z$/;

@Injectable({
  providedIn: 'root',
})
export class HttpClientWrapperService {
  private authSecretKey = 'Bearer Token';

  constructor(
    private httpClient: HttpClient,
    private navControl: NavController,
    private toastController: ToastController,
    private injector: Injector
  ) {}

  private handleError(err: unknown, url: string) {
    const enhancedError: EnhancedError = ErrorHandlerUtility.enhanceError(err);

    const error: HttpErrorResponse = err as HttpErrorResponse;
    // Exclude push-subscriptions/unsubscribe to prevent infinite recursion:
    // handleSessionExpiry() invokes that endpoint, so a 401 from it must NOT
    // re-enter this branch.
    if (
      error.status === 401 &&
      !url.includes('/me') &&
      !url.includes('/respond-to-invite') &&
      !url.includes('/invitation-preview') &&
      !url.includes('/change-password') &&
      !url.includes('/push-subscriptions/unsubscribe')
    ) {
      this.injector.get(AuthService, null)?.handleSessionExpiry();
      this.navControl.navigateRoot('/login');
    }
    if (error.status === 429) {
      this.showRateLimitMessage();
    }
    
    if (enhancedError.isNetworkError) {
      this.showNetworkErrorMessage();
    }
    
    return throwError(() => enhancedError);
  }

  private async getAuthorizationHeader(): Promise<{ headers: HttpHeaders }> {
    const token: string | null = sessionStorage.getItem('accessToken');
    return {
      headers: new HttpHeaders({
        Authorization: token ? `Bearer ${token}` : '',
      }),
    };
  }

  private async getGuestAuthorizationHeader(): Promise<{ headers: HttpHeaders }> {
    const token: string | null = sessionStorage.getItem('accessToken');
    const guestSessionId: string | null = localStorage.getItem('guestSessionId');

    if (token) {
      return {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      };
    } else if (guestSessionId) {
      return {
        headers: new HttpHeaders({
          'GUEST-SESSION-ID': guestSessionId,
        }),
      };
    } else {
      return {
        headers: new HttpHeaders({
          Authorization: '',
        }),
      };
    }
  }

  public postWithoutAuth<T, ContentType>(url: string, content: ContentType): Observable<T> {
    return this.httpClient.post<T>(url, content)
    .pipe(
      map(response => this.parseDatesInResponse(response))
    );
  }

  public getWithoutAuth<T>(url: string, params: Params = {}): Observable<T> {
    return this.httpClient.get<T>(url, { params })
    .pipe(
      map(response => this.parseDatesInResponse(response)),
      catchError(err => this.handleError(err, url))
    );
  }

  public post<T, ContentType>(url: string, content: ContentType): Observable<T> {
    return from(this.getAuthorizationHeader()).pipe(
      switchMap(headers =>
        this.httpClient.post<T>(url, content, headers).pipe(catchError(err => this.handleError(err, url)))
      )
    ).pipe(
      map(response => this.parseDatesInResponse(response))
    );
  }

  public postGuest<T, ContentType>(url: string, content: ContentType): Observable<T> {
    return from(this.getGuestAuthorizationHeader()).pipe(
      switchMap(headers =>
        this.httpClient.post<T>(url, content, headers).pipe(catchError(err => this.handleError(err, url)))
      )
    ).pipe(
      map(response => this.parseDatesInResponse(response))
    );
  }

  public get<T>(url: string, params: Params = {}): Observable<T> {
    return from(this.getAuthorizationHeader()).pipe(
      switchMap(headers =>
        this.httpClient.get<T>(url, { ...headers, params }).pipe(catchError(err => this.handleError(err, url)))
      )
    )
    .pipe(
      map(response => this.parseDatesInResponse(response))
    );
  }

  public getGuest<T>(url: string, params: Params = {}): Observable<T> {
    return from(this.getGuestAuthorizationHeader()).pipe(
      switchMap(headers =>
        this.httpClient.get<T>(url, { ...headers, params }).pipe(catchError(err => this.handleError(err, url)))
      )
    )
    .pipe(
      map(response => this.parseDatesInResponse(response))
    );
  }

  public put<T, ContentType>(url: string, content: ContentType): Observable<T> {
    return from(this.getAuthorizationHeader()).pipe(
      switchMap(headers =>
        this.httpClient.put<T>(url, content, headers).pipe(catchError(err => this.handleError(err, url)))
      )
    ).pipe(
      map(response => this.parseDatesInResponse(response))
    );
  }

  public putGuest<T, ContentType>(url: string, content: ContentType): Observable<T> {
    return from(this.getGuestAuthorizationHeader()).pipe(
      switchMap(headers =>
        this.httpClient.put<T>(url, content, headers).pipe(catchError(err => this.handleError(err, url)))
      )
    ).pipe(
      map(response => this.parseDatesInResponse(response))
    );
  }

  public patch<T, ContentType>(url: string, content: ContentType): Observable<T> {
    return from(this.getAuthorizationHeader()).pipe(
      switchMap(headers =>
        this.httpClient.patch<T>(url, content, headers).pipe(catchError(err => this.handleError(err, url)))
      )
    ).pipe(
      map(response => this.parseDatesInResponse(response))
    );
  }

  public delete<T>(url: string): Observable<T> {
    return from(this.getAuthorizationHeader()).pipe(
      switchMap(headers =>
        this.httpClient.delete<T>(url, headers).pipe(catchError(err => this.handleError(err, url)))
      )
    ).pipe(
      map(response => this.parseDatesInResponse(response))
    );
  }

  public deleteGuest<T>(url: string, params: Params = {}): Observable<T> {
    return from(this.getGuestAuthorizationHeader()).pipe(
      switchMap(headers =>
        this.httpClient.delete<T>(url, { ...headers, params }).pipe(catchError(err => this.handleError(err, url)))
      )
    )
    .pipe(
      map(response => this.parseDatesInResponse(response))
    );
  }

  public postBlob<ContentType>(url: string, content: ContentType): Observable<Blob> {
    return from(this.getAuthorizationHeader()).pipe(
      switchMap(headers =>
        this.httpClient.post(url, content, {
          ...headers,
          responseType: 'blob'
        }).pipe(catchError(err => this.handleError(err, url)))
      )
    );
  }

  public postText<ContentType>(url: string, content: ContentType): Observable<string> {
    return from(this.getAuthorizationHeader()).pipe(
      switchMap(headers =>
        this.httpClient.post(url, content, {
          ...headers,
          responseType: 'text'
        }).pipe(catchError(err => this.handleError(err, url)))
      )
    );
  }

  public deleteFav<T, ContentType>(url: string, content: ContentType): Observable<T> {
    return this.httpClient.delete<T>(url, { body: content })
    .pipe(map(response => this.parseDatesInResponse(response)));
  }

  /**
   * Parses date strings in response to Date objects.
   * @param object The object to parse.
   * @returns The object with parsed dates.
   */
  private parseDatesInResponse<T>(response: T) {
    const object: T = response as T;

    if (Array.isArray(object)) {
      return object.map(item => this.parseDates(item)) as T;
    }
        
    return this.parseDates(object);
  }

  private parseDates<T>(object: T): T {
    if (!object || typeof object !== 'object') {
      return object;
    }

    const obj: Record<string, unknown> = object as Record<string, unknown>;
    Object.keys(obj).forEach(key => {
      if (obj[key] === null) {
        return;
      }

      if (obj[key] instanceof Object) {
        obj[key] = this.parseDates(obj[key]);
      }

      if (typeof obj[key] === 'string' && dateRegex.test(obj[key] as string)) {
        obj[key] = new Date(obj[key] as string) as Date;
      }
    })

    return object;
  }

  private async showRateLimitMessage() {
    const toast: HTMLIonToastElement = await this.toastController.create({
      message: 'Too many requests. Please wait a moment before trying again.',
      duration: 10000,
      color: 'danger',
      position: 'top'
    });
    toast.present();
  }

  private async showNetworkErrorMessage() {
    const toast: HTMLIonToastElement = await this.toastController.create({
      message: 'Network connection issue. Please check your internet connection.',
      duration: 5000,
      color: 'warning',
      position: 'top',
      buttons: [
        {
          text: 'Dismiss',
          role: 'cancel'
        }
      ]
    });
    toast.present();
  }
}
